import { createContext, useContext, useEffect, useState, useRef } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";

const CartCtx = createContext(null);
const CART_KEY = "aura_cart_v1";
const WISH_KEY = "aura_wishlist_v1";

function loadJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch { return fallback; }
}

export function CartProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState(() => loadJSON(CART_KEY, []));
  const [wishlist, setWishlist] = useState(() => loadJSON(WISH_KEY, []));
  const [drawerOpen, setDrawerOpen] = useState(false);
  const syncRef = useRef();

  useEffect(() => { localStorage.setItem(CART_KEY, JSON.stringify(items)); }, [items]);
  useEffect(() => { localStorage.setItem(WISH_KEY, JSON.stringify(wishlist)); }, [wishlist]);

  // Sync cart to backend (debounced) when user is logged in
  useEffect(() => {
    if (!user) return;
    if (syncRef.current) clearTimeout(syncRef.current);
    syncRef.current = setTimeout(() => {
      api.post("/cart/sync", { items }).catch(() => {});
    }, 1500);
    return () => clearTimeout(syncRef.current);
  }, [items, user]);

  const addToCart = (product, qty = 1) => {
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.id === product.id);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], quantity: next[idx].quantity + qty };
        return next;
      }
      return [...prev, {
        id: product.id, name: product.name, price: product.price,
        image: product.images?.[0], quantity: qty
      }];
    });
    setDrawerOpen(true);
  };
  const updateQty = (id, q) => setItems((prev) => prev.map((p) => p.id === id ? { ...p, quantity: Math.max(1, q) } : p));
  const removeItem = (id) => setItems((prev) => prev.filter((p) => p.id !== id));
  const clearCart = () => {
    setItems([]);
    if (user) api.post("/cart/clear").catch(() => {});
  };

  const toggleWishlist = (product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) return prev.filter((p) => p.id !== product.id);
      return [...prev, { id: product.id, name: product.name, price: product.price, image: product.images?.[0] }];
    });
  };
  const inWishlist = (id) => !!wishlist.find((p) => p.id === id);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartCtx.Provider value={{
      items, wishlist, subtotal, count, drawerOpen, setDrawerOpen,
      addToCart, updateQty, removeItem, clearCart, toggleWishlist, inWishlist
    }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
